import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import SiteHeader from '../components/SiteHeader';
import StatusBadge from '../components/StatusBadge';
import RequireSession from '../components/RequireSession';
import {
  apiErrorMessage,
  apiErrorStatus,
  fetchMyOrder,
  requestOrderPayoutAddressChange,
  type PayoutAddressChangeRequested,
  type MyOrderView,
} from '../lib/api';
import { startCheckout } from '../lib/saturnshift';
import {
  usePayoutAddressValidation,
  KIND_NAMES,
} from '../hooks/usePayoutAddressValidation';
import { formatCents, formatTermDays } from '../lib/format';
import { miningModeLabel } from '../lib/miningModes';
import { describeOrder, describePayment, describeProvisioning } from '../lib/orderStatus';

/**
 * One order, for the customer who owns it.
 *
 * Everything shown here comes from the server's own representation of the order,
 * including the FROZEN purchase snapshot -- the tier, effective rates and odds as they
 * stood when the order was placed. Nothing is recomputed from current pricing or current
 * difficulty, because that would quietly show different numbers than the customer
 * agreed to.
 *
 * Authorization is server-side: the account API scopes every lookup to the signed-in
 * customer, so an order belonging to someone else returns the same 404 as one that does
 * not exist. This page cannot widen that.
 */

function Row({ label, value, testId }: { label: string; value: React.ReactNode; testId?: string }) {
  return (
    <div className="mm-summary-row">
      <dt className="mm-summary-label">{label}</dt>
      <dd className="mm-summary-value" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

function OrderStatusContent() {
  const { orderNumber } = useParams<{ orderNumber: string }>();

  const [view, setView] = useState<MyOrderView | null>(null);

  /*
   * BUMPED TO ASK AGAIN. Opening this page reconciles an unpaid order against the payment
   * processor; when that cannot be reached the answer is "confirming", which is temporary
   * and worth retrying. Re-running the load is the whole retry -- there is no separate
   * endpoint and nothing is retried client-side.
   */
  const [reloadToken, setReloadToken] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  /*
   * THE SATURNSHIFT HANDOFF, WHICH IS NOT THE ORDER.
   *
   * Its own state, and deliberately unable to disturb `view`: a failed handoff means
   * "we could not open the payment page", never "your order is gone". Retrying re-runs
   * the handoff for the SAME persisted order -- it must never reach POST /api/orders,
   * which would mint a second order number and, for a capped discount, take a second
   * reservation for a customer buying one thing.
   */
  const [handoffState, setHandoffState] = useState<
    { status: 'idle' } | { status: 'starting' } | { status: 'failed' }
  >({ status: 'idle' });

  /*
   * CHANGING WHERE THIS ORDER PAYS.
   *
   * Its own small state machine, deliberately separate from the order: a failed update
   * means "we could not move it", never "your order changed". Nothing on screen is
   * replaced until the server confirms, so a customer never sees an address their
   * rewards are not actually going to.
   */
  const [editingPayout, setEditingPayout] = useState(false);
  const [payoutInput, setPayoutInput] = useState('');
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [payoutError, setPayoutError] = useState<string | null>(null);
  /*
   * The pending request, once one has been made. Replaces the old `payoutSaved` flag:
   * "saved" was the wrong word for an action that now completes in the customer's inbox.
   */
  const [payoutPending, setPayoutPending] = useState<PayoutAddressChangeRequested | null>(null);

  /*
   * THE SAME VERDICT THE CONFIGURATOR USES, on the same authoritative endpoint.
   *
   * Hooks cannot be called conditionally, so this runs whether or not the editor is
   * open -- harmless, because an empty value asks nothing.
   */
  const payoutCheck = usePayoutAddressValidation(payoutInput);

  useEffect(() => {
    if (!orderNumber) return undefined;

    const controller = new AbortController();

    fetchMyOrder(orderNumber, controller.signal)
      .then(setView)
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (apiErrorStatus(caught) === 404) {
          setNotFound(true);
          return;
        }
        setError(apiErrorMessage(caught, 'We could not load that order.'));
      });

    return () => controller.abort();
  }, [orderNumber, reloadToken]);

  if (notFound) {
    return (
      /*
       * ONE MESSAGE FOR TWO CAUSES, DELIBERATELY. The server answers the same 404 for an
       * order that does not exist and one belonging to somebody else, so a reference
       * cannot be probed for existence. This page must not undo that by guessing which
       * happened.
       *
       * What it CAN do is name the far likelier innocent explanation. Orders are keyed to
       * the email typed at checkout, not to whoever was signed in at the time -- so a
       * customer with more than one address can be signed in as one and own the order as
       * another. That is exactly what happened in staging, and the old copy gave no hint
       * of it.
       */
      <div data-testid="order-not-found">
        <h1 className="mm-display">Order not found</h1>
        <p className="mm-lede mt-4">
          We couldn&rsquo;t find an order with that reference on your account.
        </p>
        <p className="mm-help mt-3">
          Orders belong to the email address used at checkout. If you used a different
          address for this one, sign in with that address instead.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/orders" className="mm-btn-secondary inline-flex">
            Back to your orders
          </Link>
          <Link to="/sign-in" className="mm-btn-secondary inline-flex">
            Use a different email
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <p role="alert" className="mm-note mm-note--danger">
        <span>{error}</span>
      </p>
    );
  }

  if (!view) {
    return (
      <p className="mm-help" role="status">
        Loading your order…
      </p>
    );
  }

  const { order, checkout, watchUrl, payment: confirmation } = view;

  const headline = describeOrder(order);
  const payment = describePayment(order);
  const provisioning = describeProvisioning(order);

  /*
   * WHETHER THIS ORDER CAN STILL BE PAID IS THE SERVER'S ANSWER, NOT A RULE REPEATED
   * HERE. The account response carries a checkout block only while `paid` remains
   * reachable from the order's payment state; a paid, failed, expired or refunded order
   * arrives without one and no CTA renders. Keeping the eligibility rule in one place is
   * what stops this page and the backend from ever disagreeing about it.
   */
  const canPay = checkout !== null;
  const handoffFailed = handoffState.status === 'failed';

  /*
   * TWO REASONS AN UNPAID ORDER MIGHT NOT OFFER CHECKOUT, AND THEY ARE NOT THE SAME.
   *
   * `processing` means the processor has the payment in flight: there is nothing to do
   * but wait, and starting another checkout would race the one already running.
   *
   * `confirming_payment` means we could not get an answer at all. The order is untouched
   * and the question is still open, so the honest offer is to ask again -- NOT to invite
   * a second payment for an order that may already be settled.
   */
  const paymentProcessing = confirmation?.status === 'processing';
  const paymentUnconfirmed = confirmation?.status === 'confirming_payment';

  /*
   * ELIGIBLE ONLY WHILE THERE IS A LIVE ALLOCATION TO MOVE.
   *
   * Paid AND mining. An unpaid order has no allocation, one still being prepared has
   * none yet, and an ended or refunded one has nothing running. The server enforces
   * exactly this and would refuse anything else; the check here is so the action does
   * not appear where it could only fail.
   */
  const canChangePayout =
    order.states?.payment === 'paid' && order.states?.provisioning === 'active';

  function openPayoutEditor() {
    // Deliberately blank rather than prefilled: the displayed value is MASKED, so
    // prefilling it would offer a string that is not a usable address.
    setPayoutInput('');
    setPayoutError(null);
    setPayoutPending(null);
    setEditingPayout(true);
  }

  function cancelPayoutEditor() {
    setEditingPayout(false);
    setPayoutInput('');
    setPayoutError(null);
  }

  /** Submit the new address. Sends nothing but the address. */
  async function submitPayoutAddress(event: React.FormEvent) {
    event.preventDefault();
    /*
     * NOTHING UNVALIDATED REACHES THE PATCH. Not merely a disabled button: the guard is
     * here too, because a form can be submitted by pressing Enter and because a stale
     * click can land while a check is still in flight. `isSubmittable` is true only when
     * the server has said yes -- not while empty, and not while checking.
     */
    if (payoutSaving || !orderNumber || !payoutCheck.isSubmittable) return;

    setPayoutSaving(true);
    setPayoutError(null);

    try {
      const result = await requestOrderPayoutAddressChange(orderNumber, payoutInput.trim());

      /*
       * THE VIEW IS NOT UPDATED, AND THAT IS THE WHOLE CHANGE.
       *
       * Nothing has moved yet. The server has recorded the request and emailed a
       * single-use link; the address changes when that link is claimed. Optimistically
       * showing the new address here would tell the customer their payout had moved when
       * it had not -- which is precisely the lie the confirmation step exists to prevent.
       *
       * The editor closes and the panel says what actually happened: check your email.
       */
      setEditingPayout(false);
      setPayoutInput('');
      setPayoutPending(result);
    } catch (caught: unknown) {
      // The server's own safe message. It never carries provider detail.
      setPayoutError(
        apiErrorMessage(caught, 'We could not update your payout address. Please try again.'),
      );
    } finally {
      setPayoutSaving(false);
    }
  }

  /** Re-open checkout for the order that already exists. Creates nothing. */
  async function beginCheckout() {
    if (!checkout || handoffState.status === 'starting') return;

    setHandoffState({ status: 'starting' });

    try {
      // The persisted order and the server's block for it -- the same handoff the
      // configurator uses, so there is exactly one implementation of this.
      await startCheckout({ order, checkout });
      // On success the widget navigates away. Nothing after this runs in practice, and
      // there is no success to record: success is a payment, which only the server sees.
    } catch {
      setHandoffState({ status: 'failed' });
    }
  }

  return (
    <>
      <p className="mm-eyebrow">Order</p>
      <h1 className="mm-display mm-numeric mt-3" data-testid="order-number">
        {order.orderNumber}
      </h1>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <StatusBadge status={headline} testId="order-status-badge" />
        <p className="mm-help">{headline.description}</p>
      </div>

      <section aria-labelledby="progress-heading" className="mm-panel mt-8 p-5 sm:p-6">
        <h2 id="progress-heading" className="mm-eyebrow">
          Progress
        </h2>

        {/* Both machines, shown separately. An order can be paid while setup has not
            started, and one combined word would hide that. */}
        <dl className="mt-4 divide-y" style={{ borderColor: 'var(--mm-border)' }}>
          <Row
            label="Payment"
            value={<StatusBadge status={payment} testId="payment-state-badge" />}
          />
          <Row
            label="Hashrate setup"
            value={<StatusBadge status={provisioning} testId="provisioning-state-badge" />}
          />
        </dl>
      </section>

      <section aria-labelledby="details-heading" className="mm-panel mt-4 p-5 sm:p-6">
        <h2 id="details-heading" className="mm-eyebrow">
          What you ordered
        </h2>

        <dl className="mt-4 divide-y" style={{ borderColor: 'var(--mm-border)' }}>
          <Row
            label="Mining power"
            value={
              order.configuration.tier
                ? `${order.configuration.tier.displayHashrate} · ${order.configuration.tier.label}`
                : `${order.configuration.hashrateTh} TH/s`
            }
            testId="detail-hashrate"
          />
          <Row
            label="Rental term"
            value={formatTermDays(order.configuration.termDays)}
            testId="detail-term"
          />
          <Row
            label="Mining mode"
            value={miningModeLabel(order.configuration.miningMode)}
            testId="detail-mode"
          />
          {/*
            THE PAYOUT FIELD, WITH ITS OWN QUIET ACTION.

            Not a `Row`, because this one can grow an editor. Idle it looks identical to
            every other row; the action sits under the value as small text rather than a
            button, so it never competes with "Watch your mining live" or the status.
          */}
          <div className="mm-summary-row">
            <dt className="mm-summary-label">Payout address</dt>
            <dd className="mm-summary-value" data-testid="detail-payout">
              <span className="font-mono text-xs">{order.payout.address}</span>

              {canChangePayout && !editingPayout && (
                <button
                  type="button"
                  onClick={openPayoutEditor}
                  data-testid="change-payout"
                  className="mm-help mt-1 block w-full text-right underline underline-offset-2 hover:text-[color:var(--mm-text)]"
                >
                  Change payout address
                </button>
              )}
            </dd>
          </div>
          <Row
            label="Total"
            value={formatCents(order.pricing.totalCents, order.pricing.currency)}
            testId="detail-total"
          />
          {order.pricing.effective && (
            <Row
              label="Effective rate"
              value={`${formatCents(order.pricing.effective.centsPerDay, order.pricing.currency)} per day`}
              testId="detail-effective"
            />
          )}
          <Row
            label="Placed"
            value={new Date(order.createdAt).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
            testId="detail-created"
          />
        </dl>

        {/*
          THE EDITOR, INLINE AND COMPACT.

          Inside the same card as the field it edits, so there is no modal and no jump.
          It appears only on an eligible order and only when asked for.
        */}
        {canChangePayout && editingPayout && (
          <form
            onSubmit={(event) => void submitPayoutAddress(event)}
            className="mt-5 border-t pt-5"
            style={{ borderColor: 'var(--mm-border)' }}
            data-testid="payout-form"
          >
            <label htmlFor="new-payout-address" className="mm-label text-sm">
              New BTC payout address
            </label>

            <input
              id="new-payout-address"
              type="text"
              value={payoutInput}
              onChange={(event) => setPayoutInput(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={payoutSaving}
              data-testid="payout-input"
              className="mm-input mt-2 font-mono text-sm"
              data-state={
                payoutError || payoutCheck.state.status === 'invalid'
                  ? 'invalid'
                  : payoutCheck.state.status === 'valid'
                    ? 'valid'
                    : undefined
              }
              aria-invalid={payoutCheck.state.status === 'invalid'}
              placeholder="bc1..."
            />

            {/*
              THE LIVE VERDICT, in the same reserved-height line the configurator uses so
              the card does not resize as messages appear and disappear.
            */}
            <p
              role="status"
              aria-live="polite"
              data-testid="payout-validation"
              className="mt-1.5 min-h-[1.25rem] text-sm"
              style={{
                color:
                  payoutCheck.state.status === 'invalid'
                    ? 'var(--mm-danger)'
                    : payoutCheck.state.status === 'valid'
                      ? 'var(--mm-success)'
                      : 'var(--mm-text-subtle)',
              }}
            >
              {payoutCheck.state.status === 'checking' && 'Checking address\u2026'}
              {payoutCheck.state.status === 'valid' && (
                <>
                  <span aria-hidden="true">\u2713 </span>
                  Valid {KIND_NAMES[payoutCheck.state.kind] ?? payoutCheck.state.kind} address.
                </>
              )}
              {payoutCheck.state.status === 'invalid' && payoutCheck.state.reason}
            </p>

            {/* The server's message from the SAVE attempt, which is a different failure. */}
            {payoutError && (
              <p role="alert" className="mm-note mm-note--danger mt-3" data-testid="payout-error">
                <span>{payoutError}</span>
              </p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={payoutSaving || !payoutCheck.isSubmittable}
                data-testid="submit-payout"
                className="mm-btn-primary inline-flex items-center justify-center sm:w-auto"
              >
                {payoutSaving ? 'Updating\u2026' : 'Update payout address'}
              </button>

              <button
                type="button"
                onClick={cancelPayoutEditor}
                disabled={payoutSaving}
                data-testid="cancel-payout"
                className="mm-help underline underline-offset-2 hover:text-[color:var(--mm-text)]"
              >
                Cancel
              </button>
            </div>

            <p className="mm-help mt-3">
              This changes the payout wallet for this order only.
            </p>
          </form>
        )}

        {/*
          WHAT ACTUALLY HAPPENED, which is not "updated".

          The old banner said "Payout address updated." the moment the request returned.
          With email confirmation that sentence would be false until the customer clicks
          the link -- and they would have no reason to go and look for it.
        */}
        {payoutPending && !editingPayout && (
          <p
            role="status"
            className="mm-note mt-5"
            data-testid="payout-confirmation-sent"
          >
            <span>
              {payoutPending.emailSent
                ? 'Check your email and confirm the change. Your payout address has not changed yet.'
                : 'We could not send the confirmation email just now. Your payout address has not changed. Please try again.'}
            </span>
          </p>
        )}
      </section>

      {/*
        ==========================================================================
        THE ONE THING THIS ORDER NEEDS NEXT.

        An order page that only describes an order leaves the customer to work out what to
        do about it. A customer who created an order and never paid was genuinely stranded
        here: "Awaiting payment", accurate, with no way forward but to configure the whole
        purchase again -- a second order number, and for a capped discount a second
        reservation, for one thing they were trying to buy once.

        At most one primary action, chosen by the state the order is actually in. Paid and
        preparing has none, because there is nothing for the customer to do and nothing yet
        to watch.
      */}

      {handoffFailed && (
        <p role="alert" className="mm-note mm-note--danger mt-8" data-testid="resume-error">
          <span>
            We couldn&apos;t open the payment page just now. Your order is saved, so you can
            try again.
          </span>
        </p>
      )}

      {/*
        IN FLIGHT AT THE PROCESSOR. No action, because the only safe action is waiting:
        the webhook or the next visit to this page will settle it.
      */}
      {paymentProcessing && (
        <p role="status" className="mm-note mt-8" data-testid="payment-processing">
          <span>
            Your payment is being processed. This page will show your rental as soon as it
            completes &mdash; there is no need to pay again.
          </span>
        </p>
      )}

      {/*
        WE COULD NOT ASK. Deliberately NOT phrased as "unpaid": the order may well be
        settled and we simply do not know yet, which is exactly why no checkout is offered
        alongside this.
      */}
      {paymentUnconfirmed && (
        <div className="mt-8" data-testid="payment-unconfirmed">
          <p role="status" className="mm-note">
            <span>
              We couldn&rsquo;t confirm your payment status just now. Your order is saved
              and nothing has changed.
            </span>
          </p>
          <button
            type="button"
            onClick={() => setReloadToken((token) => token + 1)}
            data-testid="retry-payment-check"
            className="mm-btn-secondary mt-4 inline-flex items-center justify-center sm:w-auto"
          >
            Check again
          </button>
        </div>
      )}

      {canPay && (
        <div className="mt-8">
          <button
            type="button"
            onClick={() => void beginCheckout()}
            disabled={handoffState.status === 'starting'}
            data-testid="resume-checkout"
            className="mm-btn-primary inline-flex items-center justify-center sm:w-auto"
          >
            {handoffState.status === 'starting'
              ? 'Opening secure checkout…'
              : handoffFailed
                ? 'Try again'
                : 'Continue to secure checkout'}
          </button>
          {/*
            ONE SENTENCE, AND IT IS FOR THE CUSTOMER.

            It used to go on to promise that this was "the same order, nothing re-priced,
            no new order created". All three are true and all three are OUR problem: they
            describe an idempotency guarantee the tests hold us to, in vocabulary a
            customer trying to pay has no use for. Saying it out loud also invites the
            question of why it needed saying.
          */}
          <p className="mm-help mt-3" data-testid="resume-note">
            You&rsquo;ll complete payment securely with our payment provider.
          </p>
        </div>
      )}

      {/*
        POWERPOOL'S OWN LIVE VIEW, WHEN THERE IS SOMETHING LIVE TO SEE.

        The server decides whether this exists at all: null unless the allocation is
        `active` AND PowerPool is live, so a rental being prepared, one whose term has
        ended, and every mock allocation arrive without it. The browser never constructs
        this URL, never rebuilds it from the payout address or the team id, and never
        inspects it -- `customerFacingWatchUrl` is the single authority, applied once.

        With no URL there is no button. Not a disabled one, not a placeholder.
      */}
      {watchUrl && (
        <div className="mt-8">
          {/*
            COMPACT ON DESKTOP, like every other primary action on this page.

            `.mm-btn-primary` carries `w-full`, which is right for a submit inside a
            narrow card and wrong for a standalone action on a wide page: it stretched
            the whole content column and read as a banner rather than a button.
            `sm:w-auto` is a utility, so it wins over the component layer here without
            touching the shared token or any other caller.

            Full width is kept below `sm`, matching the checkout CTA.
          */}
          <a
            href={watchUrl}
            target="_blank"
            rel="noreferrer"
            className="mm-btn-primary inline-flex items-center justify-center sm:w-auto"
            data-testid="watch-live"
          >
            Watch your mining live
          </a>
        </div>
      )}

      {/*
        The custody line that sat here was removed at Sidney's request (round 2). It is
        not lost from the product: the same disclosure appears in Step 4 of the
        configurator, beside the field where the customer types the address it concerns,
        which is where a custody fact means most.
      */}

      <Link to="/orders" className="mm-btn-secondary mt-6 inline-flex">
        Back to your orders
      </Link>
    </>
  );
}

export default function OrderStatus() {
  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-2xl px-5 py-10 sm:px-8 sm:py-14 flex-1 min-w-0">
        <RequireSession>
          <OrderStatusContent />
        </RequireSession>
      </main>
    </div>
  );
}
